import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { LevelBadge } from "@/components/LevelBadge";
import { ChefLessonComposer } from "@/components/ChefLessons";
import { SafetyMondaySettings } from "@/components/SafetyMondaySettings";
import { WorkerAccounts } from "@/components/WorkerAccounts";
import { DifficultWordsReview } from "@/components/TeamReview";
import { ThemeIcon } from "@/components/ThemeIcon";

const fmt = (d) => (d ? new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "jamais");
const pctColor = (p) => (p >= 80 ? "text-emerald-700" : p >= 50 ? "text-amber-600" : "text-red-700");

export default function ChefDashboardPage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/teams/dashboard").then((r) => setD(r.data)).catch((e) => { setErr(formatApiError(e)); toast.error(formatApiError(e)); });
  }, []);

  const back = (
    <Link to="/equipe" data-testid="chef-back-btn" className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-900">
      <ArrowLeft className="w-5 h-5" /> Équipe
    </Link>
  );
  if (err) return <div className="space-y-4">{back}<p className="text-red-700 font-semibold" data-testid="chef-error">{err}</p></div>;
  if (!d) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-6" data-testid="chef-dashboard">
      {back}
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-orange-600">Tableau du chef</p>
        <h1 className="font-display text-4xl font-extrabold uppercase" data-testid="chef-team-name">{d.team.name}</h1>
      </div>

      <ChefLessonComposer />
      <SafetyMondaySettings />
      <WorkerAccounts />

      <section className="space-y-2 rise">
        <p className="flex items-center gap-2 text-xs font-bold uppercase text-slate-500"><Users className="w-4 h-4" /> Équipe ({d.members.length})</p>
        {d.members.map((m, i) => (
          <div key={`${m.name}-${i}`} className="bg-white rounded-2xl border-2 border-slate-200 p-4 space-y-2" data-testid={`chef-member-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold truncate">{m.name} <span className="text-sm font-normal text-slate-500">· {m.trade}</span></div>
                <LevelBadge level={m.level} />
              </div>
              <div className={`font-display text-3xl font-bold ${pctColor(m.average)}`} data-testid={`chef-member-average-${i}`}>{m.average}%</div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="bg-slate-50 rounded-lg py-2"><div className="font-bold text-base">{m.week_lessons}</div>cette semaine</div>
              <div className="bg-slate-50 rounded-lg py-2"><div className="font-bold text-base">{m.to_review}</div>mots à revoir</div>
              <div className="bg-slate-50 rounded-lg py-2"><div className="font-bold text-base flex items-center justify-center gap-1"><Clock className="w-3 h-3" />{fmt(m.last_active)}</div>dernière leçon</div>
            </div>
          </div>
        ))}
      </section>

      <section className="space-y-2 rise rise-1" data-testid="chef-difficult-words">
        <p className="flex items-center gap-2 text-xs font-bold uppercase text-slate-500"><AlertTriangle className="w-4 h-4 text-red-600" /> Mots difficiles de l'équipe</p>
        <DifficultWordsReview words={d.difficult_words} />
      </section>

      <section className="bg-white rounded-2xl border-2 border-slate-200 p-4 space-y-2 rise rise-2" data-testid="chef-themes">
        <p className="text-xs font-bold uppercase text-slate-500">Réussite par thème</p>
        {d.themes.map((t) => (
          <div key={t.theme} className="flex items-center gap-3" data-testid={`chef-theme-${t.theme}`}>
            <ThemeIcon theme={t.theme} className="w-5 h-5 text-orange-600" />
            <span className="w-28 text-sm font-semibold">{t.theme}</span>
            <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full ${t.percent >= 80 ? "bg-emerald-500" : t.percent >= 50 ? "bg-amber-400" : "bg-red-500"}`} style={{ width: `${t.percent}%` }} />
            </div>
            <span className="w-10 text-right text-sm font-bold">{t.percent}%</span>
          </div>
        ))}
        {d.themes.length === 0 && <p className="text-sm text-slate-600">Pas encore de leçons terminées.</p>}
      </section>
    </div>
  );
}
